import React, { useState } from "react";
import {
  Show,
  TextField,
  useTranslate,
  FunctionField,
  ReferenceField,
  ArrayField,
  Datagrid,
  ImageField,
  NumberField,
  TabbedShowLayout,
  TextInput,
  SelectInput,
  TopToolbar,
  Filter,
  useShowContext,
  SingleFieldList,
  ChipField,
  useNotify,
  useDataProvider,
  useListContext,
  useRefresh,
  required,
  SimpleForm,
  SelectArrayInput,
} from "react-admin";
import { ImportButton } from "react-admin-import-csv";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  DateField,
  Title,
  ShowOnlyTopToolbar,
  EmptyList,
  CustomList,
  EmptyMessage,
} from "../../../shared/components/index.js";
import {
  GAS_BRAND_MERCHANT_STATUSES,
  FUEL_TYPES,
} from "@local/constants/index.js";

const CustomBulkUpdateButton = ({ selectedIds = [], onUnselectItems }) => {
  const [open, setOpen] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [formValues, setFormValues] = useState({});
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const handleClickOpen = async () => {
    try {
      const { data: existingCoords } = await dataProvider.getMany(
        "Coordinate",
        {
          ids: selectedIds,
        }
      );

      if (selectedIds.length === 1) {
        const [status] = existingCoords.map(({ status }) => status || "");
        const fuelTypes = existingCoords
          .map((coord) => coord.fuel_types_as_object || [])
          .reduce((common, fuels, index) => {
            if (index === 0) return fuels;
            return common.filter((fuel) => fuels.includes(fuel));
          }, []);

        const defaultValues = {
          status: status || "", // ensure always string
          fuel_types: fuelTypes.map((f) => f.name),
        };

        setDefaultFormValues(defaultValues);
        setFormValues(defaultValues);
      } else if (selectedIds.length > 1) {
        const fuelTypesArrays = existingCoords.map((coord) =>
          (coord.fuel_types_as_object || []).map((fuel) => fuel.name)
        );

        const commonFuelTypes = fuelTypesArrays.reduce((common, fuels) => {
          return common.filter((fuel) => fuels.includes(fuel));
        }, fuelTypesArrays[0] || []);

        // Detect common status
        const statuses = existingCoords.map((coord) => coord.status || "");
        const uniqueStatuses = [...new Set(statuses)];
        const commonStatus =
          uniqueStatuses.length === 1 ? uniqueStatuses[0] : "";

        const defaultValues = {
          status: commonStatus, // always a string
          fuel_types: commonFuelTypes,
        };

        setDefaultFormValues(defaultValues);
        setFormValues(defaultValues);
      } else {
        setDefaultFormValues({ status: "", fuel_types: [] });
        setFormValues({ status: "", fuel_types: [] });
      }

      setOpen(true);
    } catch (error) {
      notify("resources.notifications.errors.Forbidden", { type: "error" });
    }
  };

  const handleClose = () => {
    setDefaultFormValues({});
    setFormValues({});
    onUnselectItems();
    setOpen(false);
    refresh();
  };

  const handleSubmit = async (formData) => {
    try {
      const { data: existingCoords } = await dataProvider.getMany(
        "Coordinate",
        {
          ids: selectedIds,
        }
      );

      let formFuels = [];

      if (Array.isArray(formData.fuel_types)) {
        if (typeof formData.fuel_types[0] === "string") {
          formFuels = formData.fuel_types;
        } else if (
          typeof formData.fuel_types[0] === "object" &&
          formData.fuel_types[0].name
        ) {
          formFuels = formData.fuel_types
            .filter((f) => f && f.name)
            .map((f) => f.name);
        }
      }
      const originalCommonFuels = existingCoords
        .map((coord) => coord.fuel_types_as_object?.map((f) => f.name) || [])
        .reduce(
          (common, fuels) => common.filter((fuel) => fuels.includes(fuel)),
          existingCoords[0]?.fuel_types_as_object?.map((f) => f.name) || []
        );

      const removedFuels = originalCommonFuels.filter(
        (fuel) => !formFuels.includes(fuel)
      );
      const addedFuels = formFuels.filter(
        (fuel) => !originalCommonFuels.includes(fuel)
      );

      await Promise.all(
        existingCoords.map((coord) => {
          const coordFuels = coord.fuel_types_as_object || [];
          const currentFuelNames = coordFuels.map((f) => f.name);

          // Remove only removedFuels from this coordinate
          const updatedFuels = currentFuelNames.filter(
            (fuel) => !removedFuels.includes(fuel)
          );

          // Add any added fuels if they’re not already present
          addedFuels.forEach((fuel) => {
            if (!updatedFuels.includes(fuel)) {
              updatedFuels.push(fuel);
            }
          });

          const shouldUpdateStatus =
            formValues.status !== "" && formValues.status !== coord.status;

          return dataProvider.update("Coordinate", {
            id: coord.id,
            data: {
              fuel_types: updatedFuels,
              ...(shouldUpdateStatus && { status: formData.status }),
            },
          });
        })
      );

      handleClose();
      notify("resources.notifications.success");
    } catch (error) {
      console.error("Update error:", error);
      handleClose();
      notify("resources.notifications.errors.Forbidden", { type: "error" });
    }
  };

  const isFormChanged =
    JSON.stringify(formValues) !== JSON.stringify(defaultFormValues);

  return (
    <>
      <Button
        onClick={handleClickOpen}
        disabled={selectedIds.length === 0}
        className="button-edit"
      >
        {t("buttons.edit")}
      </Button>
      {open && defaultFormValues && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{t("buttons.edit")}</DialogTitle>
          <DialogContent sx={{ minWidth: 400, pt: 2 }}>
            <SimpleForm
              sanitizeEmptyValues
              key={JSON.stringify(defaultFormValues)}
              onSubmit={handleSubmit}
              toolbar={false}
              defaultValues={defaultFormValues}
            >
              <SelectInput
                sx={{
                  width: "100%",
                  "@media (max-width:900px)": {
                    width: "100%",
                  },
                }}
                source="status"
                choices={Object.keys(GAS_BRAND_MERCHANT_STATUSES)
                  .filter((status) => status !== "blocked")
                  .map((status) => ({
                    id: status,
                    name: `resources.GasBrandMerchant.source.status.${status}`,
                  }))}
                onChange={(e) => {
                  return setFormValues((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }));
                }}
                validate={[selectedIds.length === 1 && required()]}
                format={(value) => {
                  if (value === defaultFormValues.status) {
                    return value;
                  } else if (value === formValues.status) {
                    return value;
                  } else if (
                    selectedIds.length === 1 &&
                    value !== defaultFormValues.status &&
                    value !== formValues.status
                  ) {
                    return defaultFormValues.status;
                  } else {
                    return "";
                  }
                }}
              />
              <SelectArrayInput
                label="resources.Coordinate.fields.fuel_types_as_object"
                source="fuel_types"
                choices={Object.keys(FUEL_TYPES).map((fuel) => ({
                  id: fuel,
                  name: fuel,
                }))}
                optionText="name"
                optionValue="id"
                validate={[selectedIds.length === 1 && required()]}
                onChange={(value) =>
                  setFormValues((prev) => ({
                    ...prev,
                    fuel_types: value,
                  }))
                }
              />
              <DialogActions>
                <Button onClick={handleClose}>
                  {t("buttons.bulk_update_cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isFormChanged}
                >
                  {t("buttons.bulk_update_save")}
                </Button>
              </DialogActions>
            </SimpleForm>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

CustomBulkUpdateButton.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.any),
  onUnselectItems: PropTypes.func,
};

const BulkActionWrapper = () => {
  const { selectedIds, onUnselectItems } = useListContext();

  return (
    <CustomBulkUpdateButton
      selectedIds={selectedIds}
      onUnselectItems={onUnselectItems}
    />
  );
};

const CoordinatesFilter = (props) => (
  <TopToolbar>
    <Filter {...props}>
      <TextInput
        source="name"
        alwaysOn
        resettable
        label="resources.Coordinate.fields.name"
      />
      <TextInput
        source="address"
        alwaysOn
        resettable
        label="resources.Coordinate.fields.address"
      />
      <SelectInput
        alwaysOn
        resettable
        source="status"
        choices={Object.keys(GAS_BRAND_MERCHANT_STATUSES).map((status) => {
          if (status === "blocked") {
            return;
          }

          return {
            id: status,
            name: `resources.GasBrandMerchant.source.status.${status}`,
          };
        })}
      />
    </Filter>
  </TopToolbar>
);

const ImportCoords = (props) => {
  const { record } = useShowContext();

  localStorage.setItem("gas_brand_merchant_id", record.id);

  const config = {
    logging: true,
    disableImportOverwrite: true,
    openAskDecide: true,
    handleClose: true,
    handleAskDecideReplace: true,
    handleAskDecideAddAsNew: true,
    handleAskDecideSkip: true,
    handleAskDecideSkipAll: true,
  };

  return (
    <TopToolbar>
      <ImportButton
        className="button-edit"
        {...props}
        {...config}
        parseConfig={{ dynamicTyping: true }}
      />
    </TopToolbar>
  );
};

const ShowCoords = (props) => {
  const { record } = useShowContext();
  const t = useTranslate();

  return (
    <ArrayField source="coordinates" perPage={10} label="">
      <CustomList
        title=" "
        filter={{ gas_brand_merchant_id: record && record.id }}
        resource="Coordinate"
        empty={<EmptyList resource="Coordinate" isCreateAllowed={true} />}
        sx={{ border: "none" }}
        filters={<CoordinatesFilter {...props} />}
        actions={<ImportCoords {...props} hasCreate />}
      >
        <Datagrid
          empty={<EmptyMessage />}
          rowClick={false}
          bulkActionButtons={<BulkActionWrapper />}
          sx={{
            "& .column-address": {
              width: "30%",
              textAlign: "center",
            },
            "& .column-lat": {
              textAlign: "center",
              width: "10%",
            },
            "& .column-long": {
              textAlign: "center",
              width: "10%",
            },
            "& .column-status": {
              textAlign: "center",
              width: "10%",
            },
            "& .column-fuel_types_as_object": {
              textAlign: "center",
              width: "30%",
            },
          }}
          optimized
        >
          <TextField source="name" label="resources.Coordinate.fields.name" />
          <TextField
            source="address"
            label="resources.Coordinate.fields.address"
          />
          <NumberField
            source="lat"
            label="resources.Coordinate.fields.lat"
            options={{ maximumFractionDigits: 25 }}
          />
          <NumberField
            source="long"
            label="resources.Coordinate.fields.long"
            options={{ maximumFractionDigits: 25 }}
          />
          <ArrayField source="fuel_types_as_object" sortable={false}>
            <SingleFieldList linkType={false}>
              <ChipField source="name" sx={{ color: "#1976d2" }} />
            </SingleFieldList>
          </ArrayField>
          <FunctionField
            source="status"
            render={(record) =>
              t(`resources.GasBrandMerchant.source.status.${record.status}`)
            }
          />
          <DateField source="created_at" />
          <DateField source="updated_at" />
        </Datagrid>
      </CustomList>
    </ArrayField>
  );
};

const showGasBrandMerchant = (props) => {
  const t = useTranslate();

  return (
    <Show title={<Title source="name" />} actions={<ShowOnlyTopToolbar />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="resources.GasBrandMerchant.source.infoTab.info">
          <ReferenceField source="gas_brand_id" reference="GasBrand">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="logo_file_id" reference="File">
            <ImageField source="url" />
          </ReferenceField>
          <TextField source="qr_type" />
          <FunctionField
            source="status"
            render={(record) =>
              t(`resources.GasBrandMerchant.source.status.${record.status}`)
            }
          />
          <ArrayField source="fuels">
            <Datagrid optimized rowClick={false} bulkActionButtons={null}>
              <TextField
                source="name"
                label="resources.GasBrandMerchant.fuels.name"
              />
              <TextField
                source="regular_price"
                label="resources.GasBrandMerchant.fuels.regular_price"
              />

              <FunctionField
                source="status"
                render={(record) =>
                  t(`resources.GasBrandMerchant.source.status.${record.status}`)
                }
              />
            </Datagrid>
          </ArrayField>
          <DateField source="created_at" />
          <DateField source="updated_at" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="resources.GasBrandMerchant.source.infoTab.coords">
          <ShowCoords {...props} />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};

export default showGasBrandMerchant;
